import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { postMerchantEmitTokenAPI } from 'src/serverApi/merchants';
import useWithAuthorities from 'src/utils/useWithAuthorities';
import permissions from 'src/constants/permissions';
import { Alert } from '@1c/ui';
import { copyToClipboard } from 'src/utils/copyToClipboard';
import Modal from 'src/components/Modal';
import Button from 'src/components/Button';
import LinkButton from 'src/components/LinkButton';
import Text from 'src/components/Text';
import MerchantInfo from './MerchantInfo';
import styles from './styles.module.scss';
import { toast } from 'react-toastify';
import InputField from 'src/components/InputField';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

const STEPS = {
  account: 'account',
  token: 'token',
};

const accountInputMask = createNumberMask({
  integerLimit: 20,
  prefix: '',
  includeThousandsSeparator: false,
  allowLeadingZeroes: true,
});

const validateAccount = (value: string | undefined) => {
  let error = null;
  if (!value) {
    error = 'Обязательное поле';
  } else if (value.length < 20) {
    error = 'Необходимо ввести 20 цифр';
  }
  return error;
};

type Props = {
  merchant: any;
  reloadMerchants: () => Promise<any>;
  alternativeAccount?: boolean;
};

const EmitTokenButton: React.FC<Props> = ({ merchant, reloadMerchants, alternativeAccount }) => {
  const { organizationUeid = '', bankUeid = '' } = useParams();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [step, setStep] = useState(alternativeAccount ? STEPS.account : STEPS.token);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState(null);
  const withAuthorities = useWithAuthorities([permissions.dataManagement]);
  const [accountNumber, setAccountNumber] = useState('');
  const [accountError, setAccountError] = useState('');
  const linkText = alternativeAccount ? 'Получить токен для другого счета' : 'Получить токен';

  const emitToken = async () => {
    try {
      setIsLoading(true);
      await withAuthorities(async () => {
        const token = await postMerchantEmitTokenAPI(
          organizationUeid,
          bankUeid,
          merchant?.ueid,
          alternativeAccount ? { alternativeAccountNumber: accountNumber } : {}
        );
        setToken(token);
        setIsDialogOpen(true);
      });
    } catch (error) {
      toast.error('Произошла ошибка при попытке выпустить токен');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyToken = () => {
    if (token) {
      copyToClipboard(token)
        .then(() => toast.success('Токен скопирован в буфер'))
        .catch(() => toast.error('Ошибка при копировании токена в буфер'));
    }
  };

  const handleLinkClick = async () => {
    if (step === STEPS.token) {
      emitToken();
    } else if (step === STEPS.account) {
      setIsDialogOpen(true);
    }
  };

  const onAccountNumberChange = (event: any) => {
    setAccountError('');
    setAccountNumber(event.currentTarget?.value);
  };

  const handleSubmitAccountNumber = () => {
    const error = validateAccount(accountNumber);
    if (error) {
      setAccountError(error);
    } else {
      emitToken();
      setStep(STEPS.token);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setToken(null);
    reloadMerchants();
  };

  return (
    <>
      <LinkButton onClick={handleLinkClick} disabled={isLoading}>
        {isLoading ? '...выпуск токена' : linkText}
      </LinkButton>
      <Modal
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        title={step === STEPS.token ? 'Токен получен' : 'Введите расчетный счет'}
        content={
          step === STEPS.token ? (
            <>
              <MerchantInfo merchant={merchant} />
              <Alert level="info" className={styles['token-alert']}>
                <Text type="h3" className={styles['token-title']}>
                  Токен
                </Text>
                <Text
                  type="p"
                  className={styles['token-body']}
                  lang="en"
                  translate="no"
                  spellCheck={false}
                >
                  {token}
                </Text>
              </Alert>
              <Text type="p" muted italic className={styles['token-instruction']}>
                Скопируйте токен и вставьте его в форму настройки СБП в программе. После закрытия
                окна получить повторно этот токен будет невозможно. Потребуется перевыпуск нового
                токена
              </Text>
            </>
          ) : (
            <InputField
              label="Расчетный счет"
              value={accountNumber}
              onChange={onAccountNumberChange}
              error={accountError}
              mask={accountInputMask}
              className={styles['input']}
            />
          )
        }
        footer={
          <>
            {step === STEPS.token ? (
              <Button onClick={handleCopyToken} className={styles['modal-button']}>
                Копировать
              </Button>
            ) : (
              <Button onClick={handleSubmitAccountNumber} className={styles['modal-button']}>
                Получить токен
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={handleCloseDialog}
              className={styles['modal-button']}
            >
              Закрыть
            </Button>
          </>
        }
      />
    </>
  );
};

export default EmitTokenButton;
